import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/shared/stores/authStore';
import type { ApiError } from '@/shared/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://portal.grupocoris.com/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 30000,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // El servidor (Apache/ModSecurity) responde 406 a PUT/PATCH/DELETE antes de
    // llegar a Laravel (y sin headers CORS). Se envían como POST con `_method`
    // en el cuerpo (method spoofing de Laravel); el header X-HTTP-Method-Override
    // también está bloqueado.
    const method = config.method?.toUpperCase();
    if (method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
      config.method = 'post';
      if (config.data instanceof FormData) {
        config.data.append('_method', method);
      } else {
        config.data = { ...(config.data ?? {}), _method: method };
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }

    const apiError: ApiError = {
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Error desconocido',
      errors: error.response?.data?.errors,
      status: error.response?.status || 500,
    };

    return Promise.reject(apiError);
  }
);

export function handleApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    return {
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      errors: error.response?.data?.errors,
      status: error.response?.status || 500,
    };
  }
  return {
    message: error instanceof Error ? error.message : 'Error desconocido',
    status: 500,
  };
}
